/**
 * Service for CBE (Crossroads Bank for Enterprises) API via backend proxy
 * All CBE API calls go through the backend proxy at /cbe/*
 * The API key is kept server-side and never exposed to the client.
 * API Documentation: https://cbeapi.be/docs/api#/
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Helper: get current peopleId from localStorage
 * @returns {number}
 * @throws {Error} If user is not logged in
 */
const getPeopleId = () => {
    const peopleId = parseInt(localStorage.getItem('userId'), 10);
    if (!peopleId) throw new Error('User not logged in');
    return peopleId;
};

/**
 * NACE code to place type mapping
 * Maps NACE activity codes to our place types
 */
const NACE_TO_PLACE_TYPE = {
    // Food & Restaurant (56.xx)
    '56': { category: 'food', type: 'restaurant', icon: 'UtensilsCrossed' },
    '5610': { category: 'food', type: 'restaurant', icon: 'UtensilsCrossed' },
    '5621': { category: 'food', type: 'catering', icon: 'ChefHat' },
    '5629': { category: 'food', type: 'catering', icon: 'ChefHat' },
    '5630': { category: 'food', type: 'bar', icon: 'Wine' },
    // Retail (47.xx)
    '47': { category: 'shopping', type: 'shop', icon: 'ShoppingBag' },
    '4711': { category: 'shopping', type: 'supermarket', icon: 'ShoppingCart' },
    '4721': { category: 'food', type: 'grocery', icon: 'Apple' },
    '4722': { category: 'food', type: 'butcher', icon: 'Beef' },
    '4723': { category: 'food', type: 'fishmonger', icon: 'Fish' },
    '4724': { category: 'food', type: 'bakery', icon: 'Croissant' },
    '4725': { category: 'food', type: 'drinks', icon: 'Wine' },
    '4726': { category: 'shopping', type: 'tobacco', icon: 'Cigarette' },
    '4761': { category: 'shopping', type: 'bookstore', icon: 'BookOpen' },
    '4771': { category: 'shopping', type: 'clothing', icon: 'Shirt' },
    '4772': { category: 'shopping', type: 'shoes', icon: 'Footprints' },
    '4773': { category: 'health', type: 'pharmacy', icon: 'Pill' },
    '4774': { category: 'health', type: 'medical_supply', icon: 'Stethoscope' },
    '4775': { category: 'shopping', type: 'cosmetics', icon: 'Sparkles' },
    '4776': { category: 'shopping', type: 'florist', icon: 'Flower2' },
    '4777': { category: 'shopping', type: 'jewelry', icon: 'Gem' },
    // Hospitality (55.xx)
    '55': { category: 'hospitality', type: 'hotel', icon: 'Hotel' },
    '5510': { category: 'hospitality', type: 'hotel', icon: 'Hotel' },
    '5520': { category: 'hospitality', type: 'vacation_rental', icon: 'Home' },
    '5530': { category: 'hospitality', type: 'camping', icon: 'Tent' },
    // Health (86.xx)
    '86': { category: 'health', type: 'healthcare', icon: 'Stethoscope' },
    '8610': { category: 'health', type: 'hospital', icon: 'Hospital' },
    '8621': { category: 'health', type: 'doctor', icon: 'Stethoscope' },
    '8622': { category: 'health', type: 'specialist', icon: 'HeartPulse' },
    '8623': { category: 'health', type: 'dentist', icon: 'SmilePlus' },
    // Sports (93.xx)
    '93': { category: 'sports', type: 'sports', icon: 'Dumbbell' },
    '9311': { category: 'sports', type: 'sports_facility', icon: 'Dumbbell' },
    '9312': { category: 'sports', type: 'sports_club', icon: 'Trophy' },
    '9313': { category: 'sports', type: 'gym', icon: 'Dumbbell' },
    // Entertainment (90.xx, 91.xx, 92.xx)
    '90': { category: 'entertainment', type: 'arts', icon: 'Theater' },
    '91': { category: 'entertainment', type: 'cultural', icon: 'Landmark' },
    '9102': { category: 'entertainment', type: 'museum', icon: 'Landmark' },
    '9103': { category: 'entertainment', type: 'historic_site', icon: 'Castle' },
    '92': { category: 'entertainment', type: 'gambling', icon: 'Dice5' },
    // Education (85.xx)
    '85': { category: 'education', type: 'school', icon: 'GraduationCap' },
    '8510': { category: 'education', type: 'primary_school', icon: 'School' },
    '8520': { category: 'education', type: 'secondary_school', icon: 'School' },
    '8530': { category: 'education', type: 'higher_education', icon: 'GraduationCap' },
    '8541': { category: 'education', type: 'training', icon: 'BookOpen' },
    // Services (69.xx - 82.xx)
    '69': { category: 'services', type: 'legal', icon: 'Scale' },
    '6910': { category: 'services', type: 'lawyer', icon: 'Scale' },
    '6920': { category: 'services', type: 'accountant', icon: 'Calculator' },
    '70': { category: 'business', type: 'consulting', icon: 'Briefcase' },
    '71': { category: 'services', type: 'architecture', icon: 'Compass' },
    '7111': { category: 'services', type: 'architect', icon: 'Compass' },
    '72': { category: 'business', type: 'research', icon: 'FlaskConical' },
    '73': { category: 'business', type: 'advertising', icon: 'Megaphone' },
    '74': { category: 'services', type: 'professional', icon: 'Briefcase' },
    '7420': { category: 'services', type: 'photography', icon: 'Camera' },
    '75': { category: 'services', type: 'veterinary', icon: 'Cat' },
    '96': { category: 'services', type: 'personal_services', icon: 'Scissors' },
    '9602': { category: 'services', type: 'hairdresser', icon: 'Scissors' },
    '9604': { category: 'services', type: 'wellness', icon: 'Spa' },
    // Construction (41.xx - 43.xx)
    '41': { category: 'business', type: 'construction', icon: 'HardHat' },
    '42': { category: 'business', type: 'civil_engineering', icon: 'Building2' },
    '43': { category: 'business', type: 'contractor', icon: 'Wrench' },
    // Transport (49.xx - 53.xx)
    '49': { category: 'transport', type: 'transport', icon: 'Truck' },
    '4932': { category: 'transport', type: 'taxi', icon: 'Car' },
    '50': { category: 'transport', type: 'water_transport', icon: 'Ship' },
    '51': { category: 'transport', type: 'air_transport', icon: 'Plane' },
    // Real estate (68.xx)
    '68': { category: 'business', type: 'real_estate', icon: 'Home' },
    // IT (62.xx, 63.xx)
    '62': { category: 'business', type: 'it_services', icon: 'Monitor' },
    '63': { category: 'business', type: 'data_services', icon: 'Database' },
    // Manufacturing (10.xx - 33.xx)
    '10': { category: 'business', type: 'food_manufacturing', icon: 'Factory' },
    '11': { category: 'business', type: 'beverage_manufacturing', icon: 'Factory' },
    // Banking & Insurance (64.xx - 66.xx)
    '64': { category: 'business', type: 'bank', icon: 'Landmark' },
    '65': { category: 'business', type: 'insurance', icon: 'Shield' },
    // Government (84.xx)
    '84': { category: 'government', type: 'public_admin', icon: 'Building' },
    // Associations (94.xx)
    '94': { category: 'social', type: 'association', icon: 'Users' },
    '9411': { category: 'social', type: 'business_association', icon: 'Briefcase' },
    '9412': { category: 'social', type: 'professional_association', icon: 'Users' },
    '9420': { category: 'social', type: 'union', icon: 'Users' },
    '9499': { category: 'social', type: 'ngo', icon: 'Heart' },
};

/**
 * Get place type from NACE activities
 * @param {Array} activities - NACE activities array
 * @returns {Object} Place type info { category, type, icon }
 */
export const getPlaceTypeFromNace = (activities) => {
    if (!activities || !Array.isArray(activities) || activities.length === 0) {
        return { category: 'business', type: 'enterprise', icon: 'Building2' };
    }

    // Try to find a match starting with the most specific code
    for (const activity of activities) {
        const code = activity.code || activity.nace_code || activity;
        if (!code) continue;

        const codeStr = code.toString();

        // Try exact match first (4 digits)
        if (NACE_TO_PLACE_TYPE[codeStr]) {
            return NACE_TO_PLACE_TYPE[codeStr];
        }

        // Try 3 digits
        if (codeStr.length >= 3 && NACE_TO_PLACE_TYPE[codeStr.substring(0, 3)]) {
            return NACE_TO_PLACE_TYPE[codeStr.substring(0, 3)];
        }

        // Try 2 digits (category level)
        if (codeStr.length >= 2 && NACE_TO_PLACE_TYPE[codeStr.substring(0, 2)]) {
            return NACE_TO_PLACE_TYPE[codeStr.substring(0, 2)];
        }
    }

    return { category: 'business', type: 'enterprise', icon: 'Building2' };
};

/**
 * Search enterprise by VAT number (numéro d'entreprise)
 * @param {string} vatNumber - VAT number (e.g., "0123.456.789" or "BE0123456789")
 * @returns {Promise<Object>} Enterprise data
 */
export const searchByVatNumber = async (vatNumber) => {
    try {
        // Clean the VAT number (remove spaces, dots, BE prefix)
        const cleanVat = vatNumber
            .replace(/\s/g, '')
            .replace(/\./g, '')
            .replace(/^BE/i, '');

        // Format as 10 digits with leading zeros if needed
        const formattedVat = cleanVat.padStart(10, '0');

        console.log('Searching CBE for VAT:', formattedVat);

        const peopleId = getPeopleId();

        const response = await fetch(`${API_BASE_URL}/cbe/company/${formattedVat}?peopleId=${peopleId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            },
            credentials: 'include'
        });

        if (response.status === 429) {
            const err = await response.json().catch(() => ({}));
            return { success: false, message: `Limite atteinte (${err.hourCount || 0}/h, ${err.dayCount || 0}/j)` };
        }

        if (!response.ok) {
            if (response.status === 404) {
                return { success: false, message: 'Entreprise non trouvée' };
            }
            if (response.status === 401) {
                return { success: false, message: 'Erreur d\'authentification API' };
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('CBE response:', result);

        if (!result.success) {
            if (result.error === 'CBE_NOT_FOUND') {
                return { success: false, message: 'Entreprise non trouvée' };
            }
            return { success: false, message: result.message || result.error || 'Erreur CBE' };
        }

        // Backend wraps the CBE response in { success, data: <cbeResponse> }
        // The CBE response itself has shape { data: { ... } }, so extract result.data.data
        const cbePayload = result.data || {};
        const company = cbePayload.data || cbePayload;

        return {
            success: true,
            data: transformCbeData(company)
        };
    } catch (error) {
        console.error('Error searching CBE:', error);
        return {
            success: false,
            message: error.message || 'Erreur lors de la recherche'
        };
    }
};

/**
 * Search enterprises by name
 * @param {string} name - Enterprise name
 * @returns {Promise<Object>} Search results
 */
export const searchByName = async (name) => {
    try {
        console.log('Searching CBE by name:', name);

        const peopleId = getPeopleId();

        const response = await fetch(`${API_BASE_URL}/cbe/search?name=${encodeURIComponent(name)}&peopleId=${peopleId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            },
            credentials: 'include'
        });

        if (response.status === 429) {
            const err = await response.json().catch(() => ({}));
            return { success: false, message: `Limite atteinte (${err.hourCount || 0}/h, ${err.dayCount || 0}/j)` };
        }

        if (!response.ok) {
            if (response.status === 404) {
                return { success: true, data: [] };
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('CBE search response:', result);

        if (!result.success) {
            if (result.error === 'CBE_NOT_FOUND') {
                return { success: true, data: [] };
            }
            return { success: false, message: result.message || result.error || 'Erreur CBE' };
        }

        // Backend wraps the CBE response in { success, data: <cbeResponse> }
        // CBE search returns either { data: [...] } or directly an array, so handle both
        const cbePayload = result.data || {};
        const items = Array.isArray(cbePayload)
            ? cbePayload
            : (Array.isArray(cbePayload.data) ? cbePayload.data : []);

        const results = items.map(item => transformCbeData(item));

        return {
            success: true,
            data: results
        };
    } catch (error) {
        console.error('Error searching CBE by name:', error);
        return {
            success: false,
            message: error.message || 'Erreur lors de la recherche'
        };
    }
};

/**
 * Transform CBE API response to our place format
 * @param {Object} cbeData - Raw CBE data
 * @returns {Object} Transformed place data
 */
const transformCbeData = (cbeData) => {
    if (!cbeData) return null;

    // Extract address from CBE data
    const address = cbeData.address || {};

    // Extract denomination (company name)
    const denomination = cbeData.denomination ||
                        cbeData.denomination_with_legal_form ||
                        cbeData.commercial_name ||
                        '';

    // Extract legal form
    const legalForm = cbeData.juridical_form ||
                     cbeData.pretty_type ||
                     '';

    // Use formatted address from BCE API directly
    const googleAddress = address.formatted_address ||
                         address.full_address ||
                         cbeData.address_formatted ||
                         '';

    // Get place type from NACE activities
    const activities = cbeData.nace_activities || [];
    const detectedType = getPlaceTypeFromNace(activities);

    return {
        placeName: denomination,
        tvaNum: formatVatNumber(cbeData.cbe_number || ''),
        googleAddress: googleAddress,
        street: address.street || '',
        number: address.street_number || '',
        box: address.box || '',
        zip: address.post_code || '',
        city: address.city || '',
        firmType: legalForm,
        phone: cbeData.contact_infos?.phone || '',
        email: cbeData.contact_infos?.email || '',
        website: cbeData.contact_infos?.web || '',
        status: cbeData.status === 'active' ? 1 : 0,
        // Additional CBE data
        cbeNumber: cbeData.cbe_number || '',
        cbeNumberFormatted: cbeData.cbe_number_formatted || '',
        startDate: cbeData.start_date || '',
        activities: activities,
        // Detected place type from NACE
        detectedCategory: detectedType.category,
        detectedType: detectedType.type,
        detectedIcon: detectedType.icon
    };
};

/**
 * Format VAT number to Belgian format
 * @param {string} number - Raw number
 * @returns {string} Formatted VAT number (BE 0123.456.789)
 */
const formatVatNumber = (number) => {
    if (!number) return '';
    const clean = number.toString().replace(/\D/g, '').padStart(10, '0');
    return `BE ${clean.slice(0, 4)}.${clean.slice(4, 7)}.${clean.slice(7, 10)}`;
};

export default {
    searchByVatNumber,
    searchByName
};
